import '../styles/AssetLibrary.css';

import { AssetMetadata, AssetOwnerCategory, AssetType } from '../models/Asset';
import {
  AssetUploading,
  VideoEncodeObservable,
  useAssetStorage
} from '../hooks/useAssetStorage';
import React, { MouseEvent, useEffect, useState } from 'react';

import AlertDialog from './AlertDialog';
import AlertSnackbar from './AlertSnackbar';
import { AssetCard } from './AssetCard';
import { AssetPreview } from './AssetPreview';
import AssetService from '../services/AssetService';
import AssetUploadDialog from './AssetUploadDialog';
import AuthService from '../services/AuthService';
import Button from '@mui/material/Button';
import ConfirmationDialog from './ConfirmationDialog';
import { CustomLoadingProgress } from '../../shared/components/CustomLoadingProgress';
import { Disable } from 'react-disable';
import Grid from '@mui/material/Unstable_Grid2';
import { LessonComponentResultPanel } from '../../content/components/LessonComponentResultPanel';
import { VIDEO_ENCODING_COMPLETION_CHECK_INTERVAL } from '../constants';
import { documentFiles } from '../../shared/services/MimeTypes';
import getMimeType from '../../shared/services/MimeTypes';
import { isEmpty } from 'lodash';
import useAssets from '../hooks/useAssets';
import { useLessonComponentContext } from '../../content/models/LessonComponentContext';
import { useLessonContext } from '../../content/models/LessonContext';
import { useVideoEncoder } from '../hooks/useVideoEncoder';

interface AssetUploaderProps {
  types: AssetType[];
  category: AssetOwnerCategory;
  assetMetadata: AssetMetadata; //* uploaded asset's metadata
  onStart: () => void;
  onCompleted: (assetMetadata: AssetMetadata) => void;
}

export function AssetUploader(props: AssetUploaderProps) {
  const [uploadingAsset, setUploadingAsset] = useState<AssetUploading>(null);
  const [uploadedAsset, setUploadedAsset] = useState<AssetMetadata>(
    props.assetMetadata
  );
  const [deletingAsset, setDeletingAsset] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState<AssetMetadata>(null);
  const [assetFile, setAssetFile] = useState<File>(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [fileType, setFileType] = useState<string>('');

  const { handleAnswerSave } = useLessonComponentContext();

  const owner = AuthService.Userkey;

  useEffect(() => {
    if (props.assetMetadata) {
      setUploadedAsset(props.assetMetadata);
    }
  }, [props.assetMetadata]);

  const { deleteAssets, deletingAssets, deleteAssetsError, deleted } =
    useAssets(props.types, props.category, false /* no need to load assets */);

  const { requestEncoding } = useVideoEncoder(AuthService.Tenant);

  const { upload } = useAssetStorage(AuthService.Tenant);

  const { mode, finalized, quizLocked, assessmentSubmitted, portfolioLocked } =
    useLessonContext();

  const preview = mode === 'preview';
  const disabled = mode === 'edit';

  const buttonLabel = ' file';
  const allTypes: AssetType[] = ['audio', 'document', 'image', 'video'];

  useEffect(() => {
    if (isEmpty(deleted)) {
      return;
    }
    if (deletingAsset) {
      //* Replacing asset shouldn't run this section.
      setDeletingAsset(false);
      setUploadedAsset(null);
      props.onCompleted(null);
    }
  }, [deleted]);

  useEffect(() => {
    if (!!deleteAssetsError && !errorMessage) {
      setErrorMessage('There was an error while deleting assets.');
    }
  }, [deleteAssetsError]);

  const showUploaderButtons =
    (uploadedAsset || assetFile) &&
    !finalized &&
    !assessmentSubmitted &&
    mode !== 'mark';

  function getAssetTypes(types: AssetType[]) {
    const includeAllTypes = allTypes.every((type) => {
      return types.includes(type);
    });
    if (includeAllTypes) {
      return buttonLabel;
    } else {
      return types.sort().join('/').concat(buttonLabel);
    }
  }

  function handleUploadClick() {
    setShowUploadDialog(true);
  }

  async function handleUpload(files: File[]) {
    setShowUploadDialog(false);
    if (!isEmpty(files)) {
      const mimeType = getMimeType(files[0].name);
      const assetType = AssetService.getAssetTypeFromMime(mimeType);
      if (props.types.includes(assetType)) {
        props.onStart();
        handleAnswerSave(true);
        if (preview) {
          setAssetFile(files[0]); //* Assuming a single file is being uploaded.
        } else {
          const assetsUploading = await upload(
            files,
            props.category === 'author' ? 'author_assets' : 'learner_assets'
          );
          setUploadingAsset(assetsUploading[0]); //* Assuming a single file is being uploaded.
        }
      } else {
        setFileType(assetType);
      }
    }
  }

  async function handleReplaceClick() {
    //* If the 'uploadedAsset' state variable is set, it means the current asset is being replaced.
    //* See handleEncodeCompleted() function, it deletes the current asset.
    setShowUploadDialog(true);
  }

  async function handleUploadCompletedAsync(assetMetadata: AssetMetadata) {
    //Submit assetMetadata as answer for all file types except video
    if (mode === 'learn' && !assetMetadata.contentType.startsWith('video/')) {
      props.onCompleted(assetMetadata);
    }
    setUploadingAsset((value) => {
      const copy = { ...value };
      copy.abortController = null;
      copy.uploadObservable = null;
      if (copy.metadata.contentType.startsWith('video/')) {
        copy.abortController = null;
        copy.uploadObservable = null;
        copy.encodeObservable = startVideoEncodingAsync(assetMetadata.assetkey);
      } else {
        if (uploadedAsset) {
          deleteAssets([uploadedAsset.assetkey], props.category);
        }
        setUploadingAsset(null);
        setUploadedAsset(assetMetadata);
      }
      return copy;
    });
  }

  function startVideoEncodingAsync(assetkey: string) {
    requestEncoding(owner, assetkey, props.category);

    return new VideoEncodeObservable((subscriber) => {
      const timerId = window.setInterval(async () => {
        try {
          //* Check if the metadata of the video uploaded is available periodically.
          const metadata = await AssetService.getAssetMetadataAsync(
            AuthService.Tenant,
            props.category,
            owner,
            assetkey
          );
          subscriber.next(metadata);
          subscriber.complete();
        } catch (error) {
          if (error.response.status === 404) {
            //* Encoding is still going, just swallow the error.
            return;
          }
          subscriber.error(error);
          throw error;
        }
      }, VIDEO_ENCODING_COMPLETION_CHECK_INTERVAL);
      subscriber.next(timerId);
    });
  }

  function handleUploadCanceled() {
    cancelAction();
  }

  function handleUploadError() {
    if (!errorMessage) {
      setErrorMessage('There was an error while uploading files.');
    }

    cancelAction();
  }

  async function handleEncodeCompleted(assetMetadata: AssetMetadata) {
    //Submit assetMetadata as answer for video file types.
    props.onCompleted(assetMetadata);
    uploadingAsset.abortController = null;
    uploadingAsset.uploadObservable = null;
    uploadingAsset.encodeObservable = null;
    if (uploadedAsset) {
      deleteAssets(
        [
          uploadedAsset.assetkey,
          //* The asset is a video, delete its thumbnail as well
          AssetService.getVideoThumbnailAssetkey(uploadedAsset.assetkey)
        ],
        props.category
      );
    }
    setUploadingAsset(null);
    setUploadedAsset(assetMetadata);
  }

  function handleEncodeError() {
    if (!errorMessage) {
      setErrorMessage('There was an error while encoding the uploaded video.');
    }

    cancelAction();
  }

  function cancelAction() {
    setUploadingAsset((value) => {
      const copy = { ...value }; //* Not deep-cloning to keep the references of the abortController and the observable.
      copy.abortController.abort();
      copy.abortController = null;
      copy.uploadObservable = null;
      return copy;
    });
  }

  function handleCloseUploadDialog() {
    setShowUploadDialog(false);
  }

  function handleDelete(_: MouseEvent<HTMLButtonElement>) {
    setDeletingAsset(true);
    setAssetToDelete(uploadedAsset);
  }

  function handleDoDelete(agreed: boolean, asset: AssetMetadata) {
    if (!agreed) {
      setDeletingAsset(false);
      return;
    }
    if (preview) {
      setDeletingAsset(false);
      setAssetFile(null);
    } else {
      const assetkeys: string[] = [asset.assetkey];
      //* If the asset is a video, delete its thumbnail as well
      if (asset?.contentType?.startsWith('video/')) {
        assetkeys.push(AssetService.getVideoThumbnailAssetkey(asset.assetkey));
      }
      deleteAssets(assetkeys, props.category);
    }
    setAssetToDelete(null);
    handleAnswerSave(false);
  }

  return (
    <>
      <Grid container direction="column" spacing={2} alignItems="center">
        {deletingAssets && <CustomLoadingProgress size={40} />}
        {uploadingAsset && (
          <Grid>
            <AssetCard
              asset={uploadingAsset}
              category={props.category}
              onUploadCompleted={handleUploadCompletedAsync}
              onUploadCanceled={handleUploadCanceled}
              onUploadError={handleUploadError}
              onEncodeCompleted={handleEncodeCompleted}
              onEncodeError={handleEncodeError}
            />
          </Grid>
        )}
        {assetFile && (
          <Grid style={{ width: '100%' }}>
            <AssetPreview file={assetFile} />
          </Grid>
        )}
        {!uploadedAsset && !assetFile && mode !== 'mark' && (
          <>
            {!finalized && !assessmentSubmitted ? (
              <Grid>
                <Disable
                  disabled={disabled || !!uploadingAsset || quizLocked}
                  disabledOpacity={1}
                >
                  <Button
                    color="primary"
                    variant="contained"
                    disabled={portfolioLocked}
                    onClick={handleUploadClick}
                  >
                    {`Choose ${getAssetTypes(props.types)}`}
                  </Button>
                </Disable>
              </Grid>
            ) : (
              <>
                <Grid style={{ width: '100%' }}>
                  <LessonComponentResultPanel
                    correct={null}
                    message={`You didn't upload any ${props.types}`}
                  />
                </Grid>
              </>
            )}
          </>
        )}
        {showUploaderButtons && (
          <Grid>
            <Disable
              disabled={
                disabled || deletingAsset || !!uploadingAsset || quizLocked
              }
              disabledOpacity={1}
            >
              <Grid container spacing={2}>
                <Grid>
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={handleReplaceClick}
                    disabled={portfolioLocked}
                  >
                    {`Choose a Different ${getAssetTypes(props.types)}`}
                  </Button>
                </Grid>
                <Grid>
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={handleDelete}
                    disabled={portfolioLocked}
                  >
                    {`Remove ${getAssetTypes(props.types)}`}
                  </Button>
                </Grid>
              </Grid>
            </Disable>
          </Grid>
        )}
      </Grid>
      {deletingAsset && (
        <ConfirmationDialog
          open={true}
          title={`Are you sure you want to delete ${props.types}?`}
          message={`This operation isn't reversible.`}
          onClose={(agreed: boolean) => {
            handleDoDelete(agreed, assetToDelete);
          }}
        />
      )}
      {showUploadDialog && (
        <AssetUploadDialog
          accept={AssetService.getAcceptFileTypes(props.types)}
          multiple={true}
          onUpload={handleUpload}
          onClose={handleCloseUploadDialog}
        />
      )}
      <AlertSnackbar
        open={!!errorMessage}
        message={errorMessage}
        severity="error"
        onClose={() => setErrorMessage('')}
      />
      {fileType && (
        <AlertDialog
          title={'Upload error'}
          message={`${fileType} file type is not allowed. Upload only ${props.types} file type.`}
          onClose={() => setFileType('')}
        />
      )}
    </>
  );
}
